import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {
  GeoFilter,
  useUpdateGeoFilter,
  useUploadAdminFile,
} from '@entities/common';
import {getBackendAssetPath, getErrorMessageFromApiError} from '@shared/api';
import {CardBox} from '@shared/components';
import {resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {FormRow} from '@widgets/common';
import {FormFields} from '../CreateGeoFilterOption/FormFields';
import {FormValues, v8nSchema} from '../CreateGeoFilterOption/v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

interface Props {
  geoFilter: GeoFilter;
  onClose: () => void;
}

export const EditGeoFilterOptionForm: FC<Props> = ({geoFilter, onClose}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateGeoFilter, isPending: isUpdating} =
    useUpdateGeoFilter();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => geoFilter.nameTranslations,
  );

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      slug: geoFilter.id,
      name: geoFilter.name,
      logoFile: null,
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'category_logo'})
        : null;

      await updateGeoFilter({
        slug: geoFilter.id,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        logoUrl: uploadedLogo?.key ?? getBackendAssetPath(geoFilter.logoUrl),
      });

      enqueueSnackbar('GEO filter updated successfully', {variant: 'success'});
      onClose();
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not update GEO filter. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <CardBox bgcolor={COLOR__WHITE} maxWidth={450}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider {...formMethods}>
          <FormFields
            existingLogoUrl={geoFilter.logoUrl}
            nameTranslations={nameTranslations}
            onNameTranslationsChange={translations => {
              setValue('name', translations[SupportedLanguage.English]);
              setNameTranslations(translations);
            }}
          />
        </FormProvider>

        <FormRow width={420} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>

          <Button
            type="submit"
            sx={{flex: 2}}
            disabled={isUpdating || isUploading}
          >
            Apply changes
          </Button>
        </FormRow>
      </form>
    </CardBox>
  );
};
